/**
 * Date Utilities
 *
 * From design-system.md and frontend TDD.
 *
 * Date grouping uses LOCAL time so that group labels ("Today", "Yesterday")
 * match the displayed time. Backend sends UTC timestamps (suffix "Z");
 * JavaScript's Date automatically converts to the browser's local timezone
 * when using getFullYear/getMonth/getDate (non-UTC getters).
 */

import { differenceInCalendarDays, formatDistanceToNow, parseISO } from 'date-fns';
import { de, enUS, fr } from 'date-fns/locale';
import type { TFunction } from 'i18next';

const locales = { de, fr, en: enUS };

/**
 * date-fns locale for an i18next language code ("de", "fr", "en", or a
 * regioned variant like "de-CH"). Falls back to German — the app's i18next
 * fallbackLng — so relative times never silently render in English inside an
 * otherwise-German UI.
 */
export function dateFnsLocale(language: string | undefined): typeof de {
  const base = (language ?? '').split('-')[0] as keyof typeof locales;
  return locales[base] ?? de;
}

/**
 * Day-granularity stamp for list rows: "Today" / "Yesterday" / "N days ago"
 * (via the shared documents.list.date.* keys), falling back to the browser's
 * locale date beyond a week. Single definition for every row-stamp consumer
 * (document list, approvals inbox, admin approval table) — each previously
 * carried its own copy, one of them in hardcoded English.
 *
 * Calendar days in the user's local timezone (this file's grouping rule),
 * not elapsed milliseconds — a 23:00 upload viewed the next morning is
 * "Yesterday", which a ms-floor would still call "Today".
 */
export function formatDayStamp(isoDate: string | null | undefined, t: TFunction): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  const diffDays = differenceInCalendarDays(new Date(), date);
  // <= 0: a timestamp ahead of the client clock (server skew) is "today",
  // never a negative "-1 days ago".
  if (diffDays <= 0) return t('documents.list.date.today');
  if (diffDays === 1) return t('documents.list.date.yesterday');
  if (diffDays < 7) return t('documents.list.date.daysAgo', { count: diffDays });
  return date.toLocaleDateString();
}

/**
 * Safely parse a date string, returning null for invalid dates.
 * Handles iOS Safari quirks with date parsing.
 */
export function safeParseDateString(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  try {
    const date = parseISO(dateString);
    // Check for Invalid Date
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch {
    return null;
  }
}

/**
 * Safely format a date string to localized date format.
 * Returns fallback string for invalid dates.
 * Handles iOS Safari quirks with date parsing.
 */
export function safeFormatDate(
  dateString: string | null | undefined,
  locale: string = 'de-CH',
  options?: Intl.DateTimeFormatOptions,
  fallback: string = '–'
): string {
  const date = safeParseDateString(dateString);
  if (!date) return fallback;
  try {
    return date.toLocaleDateString(locale, options);
  } catch {
    return fallback;
  }
}

/**
 * Safely format relative time (e.g., "vor 2 Stunden").
 * Returns fallback for invalid dates.
 * Handles iOS Safari quirks with date parsing.
 */
export function safeFormatDistanceToNow(
  dateString: string | null | undefined,
  options?: { addSuffix?: boolean; locale?: typeof de },
  fallback: string = '–'
): string {
  const date = safeParseDateString(dateString);
  if (!date) return fallback;
  try {
    return formatDistanceToNow(date, options);
  } catch {
    return fallback;
  }
}

/**
 * The civil day an instant falls on, as an ISO full-date.
 *
 * Read in UTC, which is the zone the platform writes: a certificate that takes
 * effect on 1 November 2026 carries `2026-11-01T00:00:00Z`, and reading that
 * through the browser's local getters would name 31 October west of Greenwich.
 * A bare `.slice(0, 10)` on the string gets the same answer only while the
 * platform keeps writing Z, which is a property of the server the client should
 * not be depending on.
 */
export function instantDay(instant: string): string {
  return new Date(instant).toISOString().slice(0, 10);
}


/**
 * Today in the BROWSER's calendar, as an ISO full-date.
 *
 * Local, deliberately, and the opposite choice from `instantDay` above. That
 * one names the day an instant the platform wrote falls on; this one names the
 * day the person in front of the screen is having. `toISOString()` here would
 * be UTC and hand a Swiss nurse tomorrow's date every evening — which is both
 * the floor a date picker offers and the line a due date is overdue against.
 */
export function todayCivilDate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * An ISO full-date rendered in the reader's locale.
 *
 * The parts are read out and re-assembled in the LOCAL calendar rather than
 * handed to `new Date(civilDate)`, which parses a bare `YYYY-MM-DD` as UTC
 * midnight: a due date of 2026-12-01 read that way renders as 30 November for
 * every reader west of Greenwich. A due date is a day a nurse named, and it
 * names the same day wherever it is read.
 */
export function formatCivilDate(civilDate: string, locale: string): string {
  const [year, month, day] = civilDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale);
}

/**
 * The last civil day a half-open period covers, as an ISO full-date.
 *
 * A Period is `[start, end)`, so it ends at the instant AFTER its last covered
 * moment (tricorder-go/temporal/doc.go): a certificate valid through
 * 31 December 2026 carries `end: 2027-01-01T00:00:00Z`. Rendering that instant
 * under "valid until" names a day the certificate does not cover, which is the
 * inverse of the conversion the client makes on the way in.
 */
export function periodLastDay(endInstant: string): string {
  return new Date(new Date(endInstant).getTime() - 1).toISOString().slice(0, 10);
}

/**
 * Descending comparator for ISO 8601 timestamp strings (newest first).
 * Plain codepoint comparison is the correct idiom here: ISO 8601 with a
 * fixed UTC suffix sorts chronologically as ASCII. `localeCompare` happens
 * to agree for pure-ASCII input but applies locale collation rules —
 * needless and subtly locale-dependent for machine timestamps.
 */
export function compareIsoDesc(a: string, b: string): number {
  return a > b ? -1 : a < b ? 1 : 0;
}
